import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import {
  DietaryIndicators,
  HashiTier,
  type DietaryItem,
} from '@/components/ui/dietary-indicators';
import { SourceLink } from '@/components/ui/source-link';
import { RecipeImage } from './recipe-image';
import styles from './recipe-hero.module.css';
import { scaleRecipeTimes, WAIT_DISPLAY_FLOOR_MINUTES } from '@/lib/recipes/scale-times';

/**
 * Minutes as a duration a person reads at a glance (#1004).
 *
 * `recipes.minutes` alone was fine while every number was two digits, but a
 * four-hour marinade renders as "268 min", which is a number the reader has to
 * do arithmetic on at exactly the moment they are deciding whether to cook the
 * thing at all. Under an hour nothing changes.
 */
function formatDuration(minutes: number, t: ReturnType<typeof useTranslations>): string {
  if (minutes < 60) return t('recipes.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? t('recipes.hoursOnly', { hours })
    : t('recipes.hoursMinutes', { hours, minutes: rest });
}

const TIER_LABEL_KEY = {
  high: 'dietary.tierHigh',
  medium: 'dietary.tierMedium',
  low: 'dietary.tierLow',
} as const;

/** `recipes.source_type` → its eyebrow label. `manual` and `remix` are absent on
 *  purpose: neither is an import, so neither has a source to attribute. An
 *  unknown value falls through to nothing rather than an "imported from null". */
const SOURCE_LABEL_KEY = {
  instagram: 'recipes.detail.source.instagram',
  youtube: 'recipes.detail.source.youtube',
  blog: 'recipes.detail.source.blog',
  pdf: 'recipes.detail.source.pdf',
  photo: 'recipes.detail.source.photo',
  text_paste: 'recipes.detail.source.textPaste',
} as const;

type SourceTypeKey = keyof typeof SOURCE_LABEL_KEY;

const isSourceTypeKey = (s: string | null | undefined): s is SourceTypeKey =>
  s != null && s in SOURCE_LABEL_KEY;

interface RecipeHeroProps {
  title: string;
  /** Creator's display name, rendered as a "by {name}" byline. null hides it. */
  authorName?: string | null;
  /** Creator's profile id. When set alongside `authorName`, the byline links to
   *  that author's `/authors/[id]` page; without it the byline stays plain text
   *  (e.g. the optimistic hero, which has no author yet). */
  authorUserId?: string | null;
  /** Where the recipe was imported from — `recipes.source_type`. Provenance sits
   *  beside the byline because it is the same class of fact as authorship.
   *  `manual` and `remix` are not imports and render nothing. */
  sourceType?: string | null;
  /** The imported address. Null for imports with none of their own (photo, PDF,
   *  pasted text), which render as a plain label rather than a dead link. */
  sourceUrl?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  /** Recipe UUID + variant — when both are passed and `imageUrl` is null, the
   *  hero polls /api/recipes/[id]/image-status and swaps the image in when it
   *  lands. Optional so cached/optimistic callers (e.g. optimistic-hero) stay
   *  on the static placeholder path. */
  recipeId?: string;
  variantType?: 'quick' | 'classic' | 'bougie' | string | null;
  /** A hero-image regeneration is in flight for THIS variant. `imageUrl` is
   *  non-null in that case, which is exactly why the hero could not spot it on
   *  its own (#751) — the caller that queued the job has to say so. */
  imageGenerationInFlight?: boolean;
  /** Fires when that regeneration ends — the new url, or null if the poll gave
   *  up. The page resolves its toast off this instead of polling separately. */
  onImageResolved?: (imageUrl: string | null) => void;
  /** Recipe slug — used to derive the `view-transition-name` that pairs the
   *  card thumbnail with the hero image during navigation. */
  slug?: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  servings: number;
  /** The count `prepTimeMinutes` / `cookTimeMinutes` are written for. When
   *  `servings` differs, prep scales linearly and cook grows by the variant's
   *  batch-bound minutes (#1000, `scaleRecipeTimes`). Omit to show the times
   *  as written — the optimistic hero has no base to scale against. */
  baseServings?: number;
  /** Minutes of steps whose clock is set by pan capacity — see above. */
  batchBoundMinutes?: number;
  /** Minutes of `passive` steps — marinating, proofing, chilling, resting
   *  (#1004). Not inside prep or cook, and does not scale with servings. When
   *  it is material the headline becomes start-to-plate rather than hands-on,
   *  because a 50-minute total on a four-hour marinade is the wrong answer to
   *  the question the reader is asking. */
  waitMinutes?: number;
  /** What ONE serving physically is ("1 scone", "2 cookies"). Null when the
   *  dish has no discrete unit — the sub-line is then omitted rather than
   *  filled with "1 portion". Scale-invariant: it stays true at every serving
   *  count, which is why it reads "1 scone each" and not a batch total (#830). */
  servingUnit?: string | null;
  /** Per-serving calories of the active variant. Null when nutrition is unavailable. */
  caloriesPerServing?: number | null;
  categoryName?: string | null;
  isVegan?: boolean;
  isVegetarian?: boolean;
  isGlutenFree?: boolean;
  isDairyFree?: boolean;
  isHashimotoFriendly?: boolean;
  hashimotoScore?: number;
  /** Whether the viewer has the Hashimoto dietary preference active. */
  showHashimotoScore?: boolean;
  /** Servings counter + action buttons — rendered in the right column on desktop, hidden on mobile (FAB handles mobile). */
  controls?: React.ReactNode;
  /** Cookbook + meal-plan buttons shown below the meta box on mobile only (desktop uses the controls slot). */
  mobileActions?: React.ReactNode;
  /** Tiny overlay actions painted in the top-right corner of the hero image
   *  (e.g. the Share affordance). Kept compact so it doesn't compete with the
   *  dish photo. */
  imageOverlayActions?: React.ReactNode;
  /** Tiny overlay actions painted in the top-LEFT corner of the hero image
   *  (e.g. Save-to-cookbook). Pairs with imageOverlayActions so Share and Save
   *  sit in opposite corners without crowding. */
  imageOverlayActionsStart?: React.ReactNode;
  /** Called when the Serves meta item is tapped — mobile only affordance to open the FAB sheet. */
  onServingsClick?: () => void;
  /** Inline serving counter embedded in the Serves meta cell on desktop, where the
   *  picker is immediately interactive (no edit affordance). Hidden on mobile, which
   *  uses the tappable value + FAB sheet instead. */
  servingsControl?: React.ReactNode;
}

export function RecipeHero({
  title,
  authorName,
  authorUserId,
  sourceType,
  sourceUrl,
  description,
  imageUrl,
  recipeId,
  variantType,
  imageGenerationInFlight,
  onImageResolved,
  slug,
  prepTimeMinutes,
  cookTimeMinutes,
  servings,
  baseServings,
  batchBoundMinutes = 0,
  waitMinutes = 0,
  servingUnit,
  caloriesPerServing,
  categoryName,
  isVegan,
  isVegetarian,
  isGlutenFree,
  isDairyFree,
  isHashimotoFriendly,
  hashimotoScore,
  showHashimotoScore = false,
  controls,
  mobileActions,
  imageOverlayActions,
  imageOverlayActionsStart,
  onServingsClick,
  servingsControl,
}: RecipeHeroProps) {
  const t = useTranslations();
  const times = scaleRecipeTimes({
    prepTimeMinutes,
    cookTimeMinutes,
    batchBoundMinutes,
    waitMinutes,
    baseServings: baseServings ?? servings,
    servings,
  });
  // Below the floor, passive minutes are part of the flow (a 3-minute rest, a
  // 5-minute steep) and the hero behaves exactly as it did before.
  const showsWait = times.waitMinutes >= WAIT_DISPLAY_FLOOR_MINUTES;
  const sourceLabelKey = isSourceTypeKey(sourceType) ? SOURCE_LABEL_KEY[sourceType] : null;
  const hashimotoTier =
    showHashimotoScore && hashimotoScore != null
      ? hashimotoScore >= 80 ? 'high' : hashimotoScore >= 50 ? 'medium' : 'low'
      : null;

  const dietaryItems: DietaryItem[] = [];
  if (isVegan) dietaryItems.push({ key: 'vegan', label: t('dietary.vegan') });
  else if (isVegetarian) dietaryItems.push({ key: 'vegetarian', label: t('dietary.vegetarian') });
  if (isGlutenFree) dietaryItems.push({ key: 'glutenFree', label: t('dietary.glutenFree') });
  if (isDairyFree) dietaryItems.push({ key: 'dairyFree', label: t('dietary.dairyFree') });

  return (
    <div className={styles.hero}>
      <div
        className={styles.imageWrapper}
        style={slug ? { viewTransitionName: `recipe-img-${slug}` } : undefined}
        data-morph-target={slug}
      >
        <RecipeImage
          // Remount per variant: RecipeImage seeds its displayed image into
          // local state once at mount, so without a fresh instance the hero
          // would keep showing the previous variant's image after a switch.
          key={variantType ?? 'no-variant'}
          recipeId={recipeId}
          variantType={variantType}
          initialImageUrl={imageUrl}
          generationInFlight={imageGenerationInFlight}
          onImageResolved={onImageResolved}
          alt={title}
          priority
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 75vw, 800px"
        />
        {imageOverlayActionsStart && (
          <div className={styles.imageOverlayActionsStart}>{imageOverlayActionsStart}</div>
        )}
        {imageOverlayActions && (
          <div className={styles.imageOverlayActions}>{imageOverlayActions}</div>
        )}
      </div>

      <div className={styles.content}>
        {(categoryName || authorName || sourceLabelKey) && (
          <p className={styles.eyebrow}>
            {categoryName && <span className={styles.category}>{categoryName}</span>}
            {authorName && (
              authorUserId ? (
                <Link href={`/authors/${authorUserId}`} className={styles.bylineLink}>
                  {t('recipes.detail.byAuthor', { name: authorName })}
                </Link>
              ) : (
                <span className={styles.byline}>
                  {t('recipes.detail.byAuthor', { name: authorName })}
                </span>
              )
            )}
            {sourceLabelKey && (
              <SourceLink
                label={t(sourceLabelKey)}
                href={sourceUrl ?? null}
                ariaLabel={sourceUrl ? t('recipes.detail.source.ariaLink', { source: t(sourceLabelKey) }) : undefined}
              />
            )}
          </p>
        )}

        <h1 className={styles.title}>{title}</h1>

        {(dietaryItems.length > 0 || hashimotoTier) && (
          <div className={styles.dietaryBadges}>
            <DietaryIndicators
              items={dietaryItems}
              density="hero"
              label={t('recipes.dietaryInformation')}
            />
            {hashimotoTier && (
              <HashiTier
                tier={hashimotoTier}
                showLabel
                label={t('dietary.hashimoto')}
                shortLabel={t(TIER_LABEL_KEY[hashimotoTier])}
                srLabel={
                  isHashimotoFriendly
                    ? t('dietary.hashimotoFullySafe')
                    : t('dietary.hashimotoPartialHero', { percent: hashimotoScore ?? 0 })
                }
              />
            )}
          </div>
        )}

        {description && (
          <p className={styles.description}>{description}</p>
        )}

        <div className={styles.meta} role="list" aria-label={t('recipes.details')}>
          <div className={styles.metaItem} role="listitem">
            <Icon name="timer" className={styles.metaIcon} size={16} strokeWidth={1.5} />
            <div>
              <span className={styles.metaLabel}>{t('recipes.totalTime')}</span>
              {/* Start-to-plate once waiting is material, hands-on otherwise.
                  A marinade the reader cannot see is the one time fact that
                  ruins an evening (#1004). */}
              <span className={styles.metaValue}>
                {formatDuration(showsWait ? times.elapsedTimeMinutes : times.totalTimeMinutes, t)}
              </span>
              <span className={styles.metaSub}>
                {times.prepTimeMinutes} {t('recipes.stages.prep')} · {times.cookTimeMinutes} {t('recipes.stages.cook')}
                {showsWait && <> · {formatDuration(times.waitMinutes, t)} {t('recipes.stages.wait')}</>}
                {times.scaled && <> · {t('recipes.timeScaledFor', { count: servings })}</>}
              </span>
            </div>
          </div>
          <div className={styles.metaItem} role="listitem">
            <Icon name="users" className={styles.metaIcon} size={16} strokeWidth={1.5} />
            <div>
              <span className={styles.metaLabel}>{t('recipes.serves')}</span>
              {/* Desktop: inline, immediately-interactive counter */}
              {servingsControl && (
                <span className={styles.servesControl}>{servingsControl}</span>
              )}
              {/* Mobile: tappable value opens the FAB sheet */}
              {onServingsClick ? (
                <button
                  type="button"
                  className={styles.servesBtn}
                  onClick={onServingsClick}
                  aria-label={t('recipes.servesAria', { count: servings })}
                >
                  <span className={styles.metaValue}>
                    {servings}
                    <Icon name="edit" className={styles.servesBtnIcon} size={11} strokeWidth={2} />
                  </span>
                </button>
              ) : (
                !servingsControl && <span className={styles.metaValue}>{servings}</span>
              )}
              {/* What one of those servings is. Composes the same metaSub the
                  time cell uses — no new element. Omitted entirely when the
                  dish has no discrete unit (#830). */}
              {servingUnit && (
                <span className={styles.metaSub}>{t('recipes.servingUnitEach', { unit: servingUnit })}</span>
              )}
            </div>
          </div>
          <div className={styles.metaItem} role="listitem">
            <Icon name="flame" className={styles.metaIcon} size={16} strokeWidth={1.5} />
            <div>
              <span className={styles.metaLabel}>{t('recipes.calories')}</span>
              <span className={styles.metaValue}>
                {caloriesPerServing != null
                  ? t('recipes.kcal', { count: Math.round(caloriesPerServing) })
                  : t('recipes.notAvailableShort')}
              </span>
              {caloriesPerServing != null && (
                <span className={styles.metaSub}>{t('recipes.perServingShort')}</span>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only inline actions — shown below meta box; hidden on desktop (controls slot handles desktop) */}
        {mobileActions && (
          <div className={styles.mobileActions}>{mobileActions}</div>
        )}

        {/* Desktop controls slot — hidden on mobile via CSS; FAB handles mobile */}
        {controls && (
          <div className={styles.controls}>{controls}</div>
        )}
      </div>
    </div>
  );
}
